
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useUpdateUserProfile } from '@/hooks/use-profile';
import { UserProfile } from '@/types/database.types';
import PhoneNumberField from './PhoneNumberField';

interface UserProfileFormProps {
  profile: UserProfile & { email?: string };
  onClose?: () => void;
}

const UserProfileForm: React.FC<UserProfileFormProps> = ({ profile, onClose }) => {
  const [formData, setFormData] = useState({
    first_name: profile.first_name || '',
    last_name: profile.last_name || '',
    phone_number: profile.phone_number || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { toast } = useToast();
  const updateProfileMutation = useUpdateUserProfile();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (formData.phone_number && !isValidPhoneNumber(formData.phone_number)) {
      newErrors.phone_number = 'Please enter a valid Ethiopian phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidPhoneNumber = (phone: string) => {
    // Remove all non-digit characters except +
    const cleaned = phone.replace(/[^\d+]/g, '');
    
    console.log('Validating phone number:', { original: phone, cleaned });
    
    // Check various Ethiopian phone number formats
    const patterns = [
      /^\+251[19]\d{8}$/, // +251912345678
      /^251[19]\d{8}$/, // 251912345678
      /^0[19]\d{8}$/, // 0912345678
      /^[19]\d{8}$/, // 912345678
      /^07\d{8}$/, // 07XXXXXXXX (alternative telco format)
      /^\+2517\d{8}$/, // +2517XXXXXXXX (alternative telco format)
    ];
    
    const isValid = patterns.some(pattern => {
      const matches = pattern.test(cleaned);
      console.log(`Pattern ${pattern} matches ${cleaned}:`, matches);
      return matches;
    });
    
    console.log('Final validation result:', isValid);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await updateProfileMutation.mutateAsync({
        userId: profile.id,
        updates: {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          phone_number: formData.phone_number.trim() || null,
        }
      });

      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully.",
      });

      onClose?.();
    } catch (error) {
      toast({
        title: "Update Failed",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Edit Profile</CardTitle>
        <CardDescription>
          Update your personal information and phone number for Telegram access.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                className={errors.first_name ? 'border-red-500' : ''}
              />
              {errors.first_name && (
                <div className="text-sm text-red-600">{errors.first_name}</div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                className={errors.last_name ? 'border-red-500' : ''}
              />
              {errors.last_name && (
                <div className="text-sm text-red-600">{errors.last_name}</div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email (Read-only)</Label>
            <Input
              id="email"
              value={profile.email || 'No email'}
              disabled
              className="bg-gray-50"
            />
            <div className="text-xs text-gray-500">
              Email is managed through the authentication system
            </div>
          </div>

          <PhoneNumberField
            value={formData.phone_number}
            onChange={(value) => setFormData(prev => ({ ...prev, phone_number: value }))}
            error={errors.phone_number}
          />

          <div className="flex gap-2 justify-end">
            {onClose && (
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
            )}
            <Button 
              type="submit" 
              disabled={updateProfileMutation.isPending}
            >
              {updateProfileMutation.isPending ? 'Updating...' : 'Update Profile'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default UserProfileForm;
